import React, { useRef, useState } from 'react';
import { Upload, FileText, CheckCircle, XCircle, Loader2 } from 'lucide-react';

/**
 * Reusable CSV upload card.
 *
 * Props:
 *   title       {string}   – card heading, e.g. "Deals"
 *   description {string}   – subtitle / required columns hint
 *   onUpload    {function} – async (file) => response object
 *   accept      {string}   – file accept string (default ".csv")
 */
export default function CsvUploadCard({ title, description, onUpload, accept = '.csv' }) {
  const inputRef              = useRef(null);
  const [file, setFile]       = useState(null);
  const [status, setStatus]   = useState('idle'); // idle | loading | success | error
  const [result, setResult]   = useState(null);
  const [dragging, setDragging] = useState(false);

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    setStatus('idle');
    setResult(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const handleUpload = async () => {
    if (!file || status === 'loading') return;
    setStatus('loading');
    setResult(null);
    try {
      const res = await onUpload(file);
      setResult(res);
      setStatus('success');
    } catch (err) {
      setResult({ message: err?.response?.data?.message || err.message || 'Upload failed' });
      setStatus('error');
    }
  };

  const reset = () => {
    setFile(null);
    setStatus('idle');
    setResult(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const StatusIcon = status === 'success' ? CheckCircle
    : status === 'error' ? XCircle
    : null;

  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{ borderColor: '#D7B472', backgroundColor: '#FFFFFF' }}
    >
      {/* Header */}
      <div className="px-5 py-4 border-b" style={{ borderColor: '#D7B472', backgroundColor: '#FDFCF9' }}>
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4" style={{ color: '#D7B472' }} />
          <span className="font-semibold text-sm" style={{ color: '#0A0A10' }}>{title}</span>
        </div>
        {description && (
          <p className="text-xs mt-1" style={{ color: '#0A0A10', opacity: 0.55 }}>{description}</p>
        )}
      </div>

      {/* Drop zone */}
      <div className="p-5">
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed cursor-pointer py-8 transition-all select-none"
          style={{
            borderColor: dragging ? '#D7B472' : 'rgba(215,180,114,0.4)',
            backgroundColor: dragging ? 'rgba(215,180,114,0.06)' : 'transparent',
          }}
        >
          <Upload className="w-6 h-6" style={{ color: '#D7B472' }} />
          {file ? (
            <div className="text-center">
              <p className="text-sm font-medium" style={{ color: '#0A0A10' }}>{file.name}</p>
              <p className="text-xs mt-0.5" style={{ color: '#0A0A10', opacity: 0.5 }}>
                {(file.size / 1024).toFixed(1)} KB — click to replace
              </p>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-sm" style={{ color: '#0A0A10', opacity: 0.7 }}>
                Drop your CSV here or <span style={{ color: '#D7B472', fontWeight: 600 }}>browse</span>
              </p>
              <p className="text-xs mt-0.5" style={{ color: '#0A0A10', opacity: 0.4 }}>.csv files only</p>
            </div>
          )}
        </div>
        <input ref={inputRef} type="file" accept={accept} className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])} />

        {/* Actions */}
        <div className="flex items-center gap-2 mt-4">
          <button
            onClick={handleUpload}
            disabled={!file || status === 'loading'}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#D7B472', color: '#FFFFFF' }}
          >
            {status === 'loading' ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
            ) : (
              <><Upload className="w-4 h-4" /> Upload</>
            )}
          </button>
          {file && status !== 'loading' && (
            <button
              onClick={reset}
              className="px-3 py-2 rounded-lg text-sm border transition-colors"
              style={{ borderColor: '#D7B472', color: '#0A0A10' }}
            >
              Clear
            </button>
          )}
        </div>

        {/* Result feedback */}
        {result && (
          <div
            className="mt-4 rounded-lg p-3 text-xs font-medium flex items-start gap-2"
            style={{
              backgroundColor: status === 'success'
                ? 'rgba(34,197,94,0.08)'
                : 'rgba(255,107,90,0.08)',
              color: status === 'success' ? '#16a34a' : '#dc2626',
            }}
          >
            {StatusIcon && <StatusIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />}
            <span>
              {status === 'success'
                ? result?.message || `Import successful — ${result?.data?.created ?? ''} records created`
                : result?.message || 'Upload failed'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
